import { SupportMessageType, SupportSenderType } from '../types/atendimentoEnums';
import { Conversation, ConversationLastMessagePreview } from '../types/conversation';
import { SupportMessage } from '../types/supportMessage';

const SUPPORT_MESSAGE_TYPE_BY_INDEX: SupportMessageType[] = [
  SupportMessageType.Incoming,
  SupportMessageType.Outgoing,
  SupportMessageType.Activity,
  SupportMessageType.Template,
];

const SUPPORT_SENDER_TYPE_BY_INDEX: SupportSenderType[] = [
  SupportSenderType.Contact,
  SupportSenderType.AppUser,
  SupportSenderType.Bot,
  SupportSenderType.System,
];

function readString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys)
  {
    const value = record[key];
    if (value !== undefined && value !== null)
    {
      return String(value);
    }
  }

  return '';
}

function isSupportMessageType(value: string): value is SupportMessageType {
  return Object.values(SupportMessageType).includes(value as SupportMessageType);
}

function isSupportSenderType(value: string): value is SupportSenderType {
  return Object.values(SupportSenderType).includes(value as SupportSenderType);
}

function readSupportMessageType(record: Record<string, unknown>): SupportMessageType {
  const raw = record.messageType ?? record.MessageType;

  if (typeof raw === 'number')
  {
    return SUPPORT_MESSAGE_TYPE_BY_INDEX[raw] ?? SupportMessageType.Incoming;
  }

  if (typeof raw === 'string')
  {
    if (isSupportMessageType(raw))
    {
      return raw;
    }

    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber))
    {
      return SUPPORT_MESSAGE_TYPE_BY_INDEX[asNumber] ?? SupportMessageType.Incoming;
    }
  }

  return SupportMessageType.Incoming;
}

function readSupportSenderType(record: Record<string, unknown>): SupportSenderType {
  const raw = record.senderType ?? record.SenderType;

  if (typeof raw === 'number')
  {
    return SUPPORT_SENDER_TYPE_BY_INDEX[raw] ?? SupportSenderType.Contact;
  }

  if (typeof raw === 'string')
  {
    if (isSupportSenderType(raw))
    {
      return raw;
    }

    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber))
    {
      return SUPPORT_SENDER_TYPE_BY_INDEX[asNumber] ?? SupportSenderType.Contact;
    }
  }

  return SupportSenderType.Contact;
}

function unwrapNestedRecord(payload: unknown, nestedKey: string): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const nested = record[nestedKey];
  if (nested && typeof nested === 'object')
  {
    return nested as Record<string, unknown>;
  }

  return record;
}

export function normalizeSupportMessage(payload: unknown): SupportMessage | null {
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const nested = record.message ?? record.Message;
  const source = (nested && typeof nested === 'object'
    ? nested
    : record) as Record<string, unknown>;

  const id = readString(source, 'id', 'Id');
  if (!id)
  {
    return null;
  }

  const createdAt = readString(source, 'createdAt', 'CreatedAt');
  const conversationId = readString(source, 'conversationId', 'ConversationId')
    || readString(record, 'conversationId', 'ConversationId');

  return {
    id,
    conversationId,
    messageType: readSupportMessageType(source),
    content: (source.content ?? source.Content) as string | null | undefined,
    senderType: readSupportSenderType(source),
    senderId: readString(source, 'senderId', 'SenderId') || null,
    isPrivate: Boolean(source.isPrivate ?? source.IsPrivate ?? false),
    status: (source.status ?? source.Status ?? null) as string | null,
    contentAttributes: (source.contentAttributes ?? source.ContentAttributes ?? null) as string | null,
    createdAt: createdAt || new Date().toISOString(),
  };
}

export function normalizeConversation(payload: unknown): Conversation | null {
  const source = unwrapNestedRecord(payload, 'conversation');
  if (!source)
  {
    return null;
  }

  const id = readString(source, 'id', 'Id');
  if (!id)
  {
    return null;
  }

  const conversation = source as unknown as Conversation;
  const unreadRaw = source.unreadCount ?? source.UnreadCount;
  const unreadCount = typeof unreadRaw === 'number' ? unreadRaw : Number(unreadRaw ?? 0);

  return {
    ...conversation,
    id,
    unreadCount: Number.isFinite(unreadCount) ? unreadCount : 0,
    agentLastSeenAt: readOptionalIso(source, 'agentLastSeenAt', 'AgentLastSeenAt'),
    assigneeLastSeenAt: readOptionalIso(source, 'assigneeLastSeenAt', 'AssigneeLastSeenAt'),
    lastNonActivityMessage: normalizeLastNonActivityMessage(
      source.lastNonActivityMessage ?? source.LastNonActivityMessage,
    ),
  };
}

function readOptionalIso(record: Record<string, unknown>, ...keys: string[]): string | null {
  const value = readString(record, ...keys);
  return value || null;
}

function normalizeLastNonActivityMessage(value: unknown): ConversationLastMessagePreview | null {
  if (!value || typeof value !== 'object')
  {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = readString(record, 'id', 'Id');
  if (!id)
  {
    return null;
  }

  return {
    id,
    messageType: readSupportMessageType(record),
    isPrivate: Boolean(record.isPrivate ?? record.IsPrivate ?? false),
    preview: (record.preview ?? record.Preview ?? null) as string | null,
    createdAt: readString(record, 'createdAt', 'CreatedAt') || new Date().toISOString(),
  };
}

const seenHubMessageIds = new Set<string>();
const MAX_SEEN_HUB_MESSAGE_IDS = 1000;

export function rememberHubSupportMessageId(id: string): boolean {
  if (seenHubMessageIds.has(id))
  {
    return false;
  }

  seenHubMessageIds.add(id);

  if (seenHubMessageIds.size > MAX_SEEN_HUB_MESSAGE_IDS)
  {
    const oldestId = seenHubMessageIds.values().next().value;
    if (oldestId)
    {
      seenHubMessageIds.delete(oldestId);
    }
  }

  return true;
}

export function getHubConversationId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const conversationId = readString(record, 'conversationId', 'ConversationId');
  if (conversationId)
  {
    return conversationId;
  }

  const message = normalizeSupportMessage(payload);
  return message?.conversationId ?? null;
}
